import { META_SCRIPT_URL } from '../constants';
import { loadScript } from '../loadScript';
import type { TrackingEvent, TrackingEventName, TrackingProvider } from '../types';

type FbqCall = unknown[];

interface Fbq {
  (...args: FbqCall): void;
  callMethod?: (...args: FbqCall) => void;
  queue: FbqCall[];
  push?: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
  disableConfigLoading?: boolean;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const META_EVENTS: Record<TrackingEventName, string> = {
  view_item: 'ViewContent',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
};

// Meta's base-code stub: calls queue until fbevents.js arrives and replays them.
function installStub(): Fbq {
  if (window.fbq) return window.fbq;
  const fbq: Fbq = Object.assign(
    (...args: FbqCall) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    },
    { queue: [], loaded: true, version: '2.0' },
  );
  fbq.push = fbq;
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
}

function eventParams(event: TrackingEvent): Record<string, unknown> {
  return {
    content_ids: [event.offerId],
    content_name: event.offerTitle,
    content_type: 'product',
    num_items: event.quantity,
    ...(event.value !== null ? { value: event.value, currency: event.currency } : {}),
  };
}

export function createMetaProvider(pixelId: string): TrackingProvider {
  return {
    install() {
      const fbq = installStub();
      // The iframe switches screens with replaceState; each would count as a new PageView.
      fbq.disablePushState = true;
      // No remote pixel config: it is what switches on automatic matching of
      // form fields (our shipping form holds names, phones and addresses) and
      // Event Setup Tool rules. Only the events sent below go out.
      fbq.disableConfigLoading = true;
      loadScript(META_SCRIPT_URL);
      // No automatic events: the pixel would otherwise scrape our buttons and page metadata.
      fbq('set', 'autoConfig', false, pixelId);
      fbq('init', pixelId);
      fbq('track', 'PageView');
    },
    track(event) {
      const name = META_EVENTS[event.name];
      if (event.orderId === null) window.fbq?.('track', name, eventParams(event));
      else window.fbq?.('track', name, eventParams(event), { eventID: event.orderId });
    },
  };
}
